import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { CssBaseline } from '@mui/material';
import {
  createTheme,
  darken,
  lighten,
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';
import { useBranding } from '../contexts/BrandContext';

type ThemeProviderProps = {
  children?: ReactNode;
};

const createBrandColor = (baseColor: Record<string, string>, main: string) => ({
  ...baseColor,
  50: lighten(main, 0.92),
  100: lighten(main, 0.82),
  200: lighten(main, 0.68),
  300: lighten(main, 0.5),
  400: lighten(main, 0.3),
  500: lighten(main, 0.12),
  600: main,
  700: darken(main, 0.12),
  800: darken(main, 0.25),
  900: darken(main, 0.38),
  950: darken(main, 0.5),
  lighter: lighten(main, 0.85),
  light: lighten(main, 0.3),
  main,
  dark: darken(main, 0.2),
  darker: darken(main, 0.4),
});

export default function ThemeProvider({ children }: ThemeProviderProps) {
  const themeMode = 'light';
  const themeDirection = 'ltr';
  const branding = useBranding();

  const brandedPalette = useMemo(() => {
    const base = themeMode === 'light' ? palette.light : palette.dark;
    const primaryColor = branding?.primaryColor;
    const secondaryColor = branding?.secondaryColor;
    if (!primaryColor && !secondaryColor) return base;
    return {
      ...base,
      ...(primaryColor
        ? {
            primary: createBrandColor(base.primary, primaryColor),
            gradients: {
              ...base.gradients,
              primary: `linear-gradient(to bottom, ${lighten(
                primaryColor,
                0.3
              )}, ${primaryColor})`,
            },
          }
        : {}),
      ...(secondaryColor
        ? {
            secondary: createBrandColor(base.secondary, secondaryColor),
          }
        : {}),
    };
  }, [themeMode, branding?.primaryColor, branding?.secondaryColor]);

  const themeOptions = useMemo(
    () => ({
      palette: brandedPalette,
      typography,
      breakpoints,
      shape: {
        borderRadius: 8,
        borderRadiusSm: 12,
        borderRadiusMd: 16,
      },
      direction: themeDirection,
      shadows: themeMode === 'light' ? shadows.light : shadows.dark,
      customShadows:
        themeMode === 'light' ? customShadows.light : customShadows.dark,
    }),
    [themeDirection, themeMode, brandedPalette]
  );

  const theme = createTheme(themeOptions as any);
  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
}
